import {
    usePostHog,
    type UserProperties,
    type TrackingProperties,
} from "./usePostHog";
import { useAdsTracking } from "./useAdsTracking";

export interface AnalyticsConfig {
    posthog?: {
        api_key: string;
        host?: string;
    };
    googleAds?: {
        conversion_id: string;
        conversion_label_signup?: string;
        conversion_label_subscription?: string;
    };
    metaAds?: {
        pixel_id: string;
    };
}

export const useAnalytics = () => {
    const posthog = usePostHog();
    const ads = useAdsTracking();

    let config: AnalyticsConfig = {};

    const init = (analyticsConfig: AnalyticsConfig) => {
        config = analyticsConfig;

        if (config.posthog?.api_key) {
            posthog.initPostHog(config.posthog.api_key, {
                api_host: config.posthog.host,
            });
        }

        if (config.googleAds?.conversion_id) {
            ads.initGoogleAds(config.googleAds.conversion_id);
        }

        if (config.metaAds?.pixel_id) {
            ads.initMetaAds(config.metaAds.pixel_id);
        }
    };

    const identifyUser = (userId: string, properties?: UserProperties) => {
        posthog.identifyUser(userId, properties);
    };

    const trackEvent = (eventName: string, properties?: TrackingProperties) => {
        posthog.trackEvent(eventName, properties);
    };

    const trackPageView = (url?: string) => {
        posthog.trackPageView(url);
    };

    const trackSignup = (
        properties?: TrackingProperties & {
            value?: number;
            transaction_id?: string;
            user_id?: string;
        },
    ) => {
        posthog.trackSignup(properties);

        ads.trackSignupConversion(
            config.googleAds?.conversion_label_signup,
            properties?.value,
            properties?.transaction_id,
            properties?.user_id,
        );
    };

    const trackSubscription = (
        plan: string,
        amount: number,
        currency: string = "CZK",
        properties?: TrackingProperties & {
            transaction_id?: string;
            team_id?: string;
        },
    ) => {
        posthog.trackSubscription(plan, amount, currency, properties);

        ads.trackSubscriptionConversion(
            config.googleAds?.conversion_label_subscription,
            amount,
            plan,
            properties?.transaction_id,
            properties?.team_id,
        );
    };

    const trackLandingPageVisit = (
        source?: string,
        medium?: string,
        campaign?: string,
    ) => {
        trackEvent("landing_page_visit", {
            utm_source: source,
            utm_medium: medium,
            utm_campaign: campaign,
            page_url: window.location.href,
            referrer: document.referrer,
        });
    };

    const trackFormStart = (formName: string) => {
        trackEvent("form_start", {
            form_name: formName,
            page_url: window.location.href,
        });
    };

    const trackFormComplete = (
        formName: string,
        properties?: TrackingProperties,
    ) => {
        trackEvent("form_complete", {
            form_name: formName,
            page_url: window.location.href,
            ...properties,
        });
    };

    return {
        init,
        identifyUser,
        trackEvent,
        trackPageView,
        trackSignup,
        trackSubscription,
        trackLandingPageVisit,
        trackFormStart,
        trackFormComplete,
    };
};
